import type { SupabaseClient } from "@supabase/supabase-js"

import { assertCanCreateWorkspace, incrementWorkspaceCount } from "@/lib/profile-usage"
import { isPassportVisaFreeEntry, isVisaOnArrival } from "@/types/visa-api"
import { populateVisaApplicationTaskFromVisaMetadata } from "@/lib/populate-visa-application-task"
import { loadUsdPerUnitFromDbOrFrankfurter } from "@/lib/fx-load"
import { populateDefaultResources } from "@/lib/populate-resources"
import { resolveWorkspaceCurrency } from "@/lib/resolve-workspace-currency"
import { ensureUniqueWorkspaceName } from "@/lib/unique-workspace-name"
import { supabase } from "@/lib/supabase"
import { parseRequirements } from "@/types/admin"
import type { TaskCategory, TaskPriority, TaskStatus } from "@/types/task"
import { findVisaMetadataRowForRoute, normalizeVisaTypeForDB } from "@/lib/visa-utils"

export interface ProvisionWorkspaceInput {
  userId: string
  nationalityCode: string
  /**
   * ISO-2 country where the trip starts (residence); defaults to {@link nationalityCode} when omitted.
   * Dashboard create flow collects this separately from passport nationality.
   */
  originCode?: string
  destinationCode: string
  destination: string
  visaType: string
  hasExistingVisa: boolean
  goal?: string
  budget?: number
  budgetCurrency?: string
  /**
   * Optional ISO currency override (same precedence as `budgetCurrency` when both set — first non-empty wins in resolver).
   */
  currency?: string
  timeline?: string
  workspaceName?: string
  /**
   * User intent from onboarding / workspace wizards (`applying_new`, `valid_existing`, …), or `unknown`
   * when unset. Do not pass raw `visa_metadata` route `visa_status` strings here — they are not user intent
   * and can wrongly trigger visa-free provisioning shortcuts.
   */
  visaStatus?: string | null
  /**
   * User's browser locale (e.g., 'en-US', 'pt-BR').
   * If omitted, defaults to 'en-US'.
   */
  locale?: string
}

export interface ProvisionWorkspaceResult {
  workspaceId: string
  taskCount: number
  error?: string
}

function visaTypeDisplayLabel(visaType: string): string {
  const v = visaType.trim().toLowerCase()
  const map: Record<string, string> = {
    visitor: "Visitor visa",
    tourist: "Tourist visa",
    work: "Work visa",
    student: "Student visa",
    family: "Family visa",
    "digital nomad": "Digital nomad visa",
    digital_nomad: "Digital nomad visa",
  }
  if (map[v]) return map[v]
  if (!visaType.trim()) return "Visa"
  return visaType.trim().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

function defaultWorkspaceTitle(destination: string, visaType: string): string {
  const dest = destination.trim() || "Trip"
  return `${dest} — ${visaTypeDisplayLabel(visaType)}`
}

async function insertSingleGeneralTask(
  client: SupabaseClient,
  workspaceId: string,
  title: string,
  description: string,
  category: TaskCategory = "general",
  priority: TaskPriority = "medium",
  /** `workspace_tasks.currency` is NOT NULL; explicit null in the payload bypasses DB default and fails. */
  currency: string = "USD"
): Promise<void> {
  const currencySafe = currency.trim() || "USD"
  const row = {
    workspace_id: workspaceId,
    title,
    description,
    status: "todo" as TaskStatus,
    priority,
    due_date: null as string | null,
    notes: null as string | null,
    cost: null as number | null,
    currency: currencySafe,
    custom_fields: {} as Record<string, unknown>,
    category,
    task_type: "general" as const,
  }
  const { error } = await client.from("workspace_tasks").insert(row as never)
  if (error) {
    throw new Error(`workspace_tasks insert failed: ${error.message}`)
  }
}

async function countWorkspaceTasks(client: SupabaseClient, workspaceId: string): Promise<number> {
  const { count, error } = await client
    .from("workspace_tasks")
    .select("id", { count: "exact", head: true })
    .eq("workspace_id", workspaceId)
  if (error) {
    return 0
  }
  return count ?? 0
}

export async function visaMetadataRowExists(
  client: SupabaseClient,
  nationalityCode: string,
  destinationCode: string,
  visaType: string
): Promise<{ exists: boolean; queryError: string | null }> {
  const { row, queryError } = await findVisaMetadataRowForRoute(client, nationalityCode, destinationCode, visaType)
  if (queryError) return { exists: false, queryError }
  return { exists: row != null, queryError: null }
}

/**
 * Single entry point for creating a workspace and seeding visa tasks from `visa_metadata`
 * (or fallback / welcome tasks per product rules). Callers pass all context; persists
 * `nationality_code` / `origin_code` on `workspaces` for downstream UI (nothing is read
 * from `onboarding_profiles` inside this function).
 */
export async function provisionWorkspace(
  input: ProvisionWorkspaceInput,
  client: SupabaseClient = supabase
): Promise<ProvisionWorkspaceResult> {
  const nat = input.nationalityCode.trim().toUpperCase()
  const origin = (input.originCode?.trim().toUpperCase() || nat) || ""
  const dest = input.destinationCode.trim().toUpperCase()
  const destinationLabel = input.destination.trim() || dest

  if (!nat || !dest) {
    throw new Error(
      `provisionWorkspace: nationalityCode and destinationCode must be non-empty ISO codes after trim (nationalityCode=${JSON.stringify(input.nationalityCode)}, destinationCode=${JSON.stringify(input.destinationCode)})`
    )
  }

  /** Map UI synonyms to DB `visa_metadata.visa_type`; preserve casing for unknown slugs. */
  const visaTypeUi = input.visaType?.trim() ?? ""
  const visaTypeRaw = normalizeVisaTypeForDB(visaTypeUi)
  const visaTypeKey = visaTypeRaw.toLowerCase()

  /**
   * Budget ISO code via `resolveWorkspaceCurrency`: explicit override, then profile preferred
   * (when set), else route `visa_metadata.destination_currency` when no preferred currency, else
   * `resolveUserCurrency` — `profiles.preferred_currency` → residency country map → `locale` /
   * browser region → USD (BRL only when residency or locale region is Brazil).
   */
  const budgetCurrency = await resolveWorkspaceCurrency(client, {
    userId: input.userId,
    explicitBudgetCurrency: input.budgetCurrency?.trim() || input.currency?.trim() || null,
    nationalityCode: nat,
    destinationCode: dest,
    visaTypeForMetadata: visaTypeRaw,
    navigatorLocale: input.locale ?? null,
  })

  let seedingError: string | undefined

  await assertCanCreateWorkspace(client, input.userId)

  const baseName =
    input.workspaceName?.trim() ||
    defaultWorkspaceTitle(destinationLabel, visaTypeRaw || "Visa")
  const name = await ensureUniqueWorkspaceName(client, input.userId, baseName)

  const { data: wsRow, error: wError } = await client
    .from("workspaces")
    .insert({
      user_id: input.userId,
      name,
      destination: destinationLabel,
      progress: 0,
      ...(nat ? { nationality_code: nat } : {}),
      ...(origin ? { origin_code: origin } : {}),
      ...(input.budget !== undefined && { budget: input.budget ?? null }),
      ...((input.budget !== undefined ||
        Boolean(input.budgetCurrency?.trim()) ||
        Boolean(input.currency?.trim())) && {
        budget_currency: budgetCurrency,
      }),
    })
    .select("id")
    .maybeSingle()

  if (wError) {
    throw new Error(`Failed to create workspace: ${wError.message}`)
  }
  const workspaceId = wsRow?.id as string | undefined
  if (!workspaceId) {
    throw new Error("Workspace insert did not return an id.")
  }

  try {
    await incrementWorkspaceCount(client, input.userId)
  } catch {
    // intentional: workspace exists; count sync failure should not block provisioning
  }

  if (
    input.budget != null &&
    Number.isFinite(Number(input.budget)) &&
    Number(input.budget) !== 0
  ) {
    try {
      const { error: bErr } = await client.from("workspace_budget").insert({
        workspace_id: workspaceId,
        total_budget: Number(input.budget),
        currency: budgetCurrency,
        notes: null,
      })
      if (bErr) throw new Error(bErr.message)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      await client
        .from("workspaces")
        .update({ budget: null, budget_currency: null })
        .eq("id", workspaceId)
      throw new Error(`workspace_budget insert failed: ${msg}`)
    }
  }

  if (input.hasExistingVisa) {
    // User already has a visa — just give them a trip organizer task
    await insertSingleGeneralTask(
      client, workspaceId,
      "Organize your trip",
      "You already have your visa. Use this workspace to plan the rest.",
      "general", "medium", budgetCurrency
    )
  } else if (!visaTypeRaw) {
    // No visa type selected — no route to look up
    await insertSingleGeneralTask(
      client, workspaceId,
      "We don't have visa requirements for this route yet",
      `You can add tasks manually or check the embassy website for ${destinationLabel}.`,
      "general", "medium", budgetCurrency
    )
  } else {
    const { row: metaRow, queryError: metaQueryError } = await findVisaMetadataRowForRoute(
      client, nat, dest, visaTypeRaw
    )
    if (metaQueryError) {
      seedingError = metaQueryError
    } else if (!metaRow) {
      await insertSingleGeneralTask(
        client, workspaceId,
        "We don't have visa requirements for this route yet",
        `You can add tasks manually or check the embassy website for ${destinationLabel}.`,
        "general", "medium", budgetCurrency
      )
    } else {
      // Determine entry outcome from the metadata row (visa-free ≠ VOA ≠ visa required)
      const routeRequirements = parseRequirements(metaRow.requirements)
      const routeIsVisaFree = isPassportVisaFreeEntry(routeRequirements.visa_status)
      const routeIsVoa = isVisaOnArrival(routeRequirements.visa_status)

      try {
        let usdPerUnit: Record<string, number> | null = null
        try { usdPerUnit = await loadUsdPerUnitFromDbOrFrankfurter(client) } catch { usdPerUnit = null }

        // DN fallback warning task
        const dnRequirements = metaRow.requirements as
          | { is_digital_nomad_fallback?: unknown; digital_nomad_note?: unknown }
          | null | undefined
        const isDigitalNomad = visaTypeKey === "digital_nomad" || visaTypeKey === "digital nomad"
        const isFallback = isDigitalNomad && dnRequirements?.is_digital_nomad_fallback === true

        if (isFallback) {
          const note = typeof dnRequirements?.digital_nomad_note === "string"
            ? dnRequirements.digital_nomad_note.trim() : ""
          await insertSingleGeneralTask(
            client, workspaceId,
            "No dedicated digital nomad visa available",
            note || "This destination doesn't have a digital nomad visa program. Tasks are based on the standard work visa, which requires employer sponsorship.",
            "general", "high", budgetCurrency
          )
        }

        // For visa-free routes, pass "applying_new" as visaStatus so
        // populateVisaApplicationTaskFromVisaMetadata doesn't early-return and
        // instead seeds the enriched prep steps (passport validity, flights,
        // insurance, etc.) which ARE the right tasks for a visa-free traveler.
        // For visa-required / VOA routes, pass the user's wizard intent as before.
        const visaStatusForPopulate = routeIsVisaFree
          ? "applying_new"
          : (input.visaStatus ?? null)

        await populateVisaApplicationTaskFromVisaMetadata(
          workspaceId,
          {
            nationality: nat,
            destinationCode: dest,
            visaType: visaTypeRaw,
            visaStatus: visaStatusForPopulate,
            budgetCurrency,
            usdPerUnit,
            locale: input.locale,
            originCode: origin,
          },
          client
        )

        // Pin an honest entry-status info task at the top
        if (routeIsVisaFree) {
          await client.from("workspace_tasks").insert({
            workspace_id: workspaceId,
            title: `No visa required — ${destinationLabel}`,
            description:
              "You don't need a visa for this trip according to verified Wundrlst data. The steps below are a travel prep checklist. Confirm entry rules with official sources before you travel.",
            status: "todo",
            priority: "low",
            category: "general",
            task_type: "general",
            sort_order: -1,
            currency: budgetCurrency,
            custom_fields: {
              requirement_source: "verified_metadata",
              cost_certainty: "unknown",
              date_kind: "planning_recommendation",
            },
          } as never)
        } else if (routeIsVoa) {
          await client.from("workspace_tasks").insert({
            workspace_id: workspaceId,
            title: `Visa on arrival — ${destinationLabel}`,
            description:
              "This is visa on arrival, not visa-free. You still need to obtain a visa at the border or airport. Fees and documents may apply. Confirm with the destination authority before you travel.",
            status: "todo",
            priority: "high",
            category: "visa_application",
            task_type: "visa_step",
            sort_order: -1,
            currency: budgetCurrency,
            custom_fields: {
              requirement_source: "verified_metadata",
              cost_certainty: "unknown",
              date_kind: "planning_recommendation",
            },
          } as never)
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        seedingError = msg
        // Fallback: if seeding fails entirely for a visa-free route, give the
        // generic task so the workspace isn't empty
        if (routeIsVisaFree) {
          try {
            await insertSingleGeneralTask(
              client, workspaceId,
              "Plan your trip",
              "Confirm entry rules with the official source before you travel. Requirements can change.",
              "general", "medium", budgetCurrency
            )
          } catch { /* non-fatal */ }
        }
      }
    }
  }

  try {
    await populateDefaultResources(workspaceId, dest || null, nat || null, client)
  } catch {
    // intentional: default resources are supplementary; workspace is still usable
  }

  const taskCount = await countWorkspaceTasks(client, workspaceId)

  return { workspaceId, taskCount, error: seedingError }
}
